import React from 'react';
import { Button } from '@/components/ui/button';
import { ChevronDown, Heart, Users, Award } from 'lucide-react';

export const HeroSection = () => {
  const scrollToContact = (e) => {
    e.preventDefault();
    const element = document.querySelector('#kontakt');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToNext = (e) => {
    e.preventDefault();
    const element = document.querySelector('#praxis');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-hero-gradient">
      {/* Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Organic shapes */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-primary/5 organic-blob animate-float" />
        <div className="absolute bottom-32 left-10 w-56 h-56 bg-secondary/10 organic-blob-2 animate-float animation-delay-200" />
        <div className="absolute top-1/2 right-1/4 w-40 h-40 bg-accent-warm/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-1/3 w-32 h-32 bg-primary-light/30 rounded-full blur-2xl" />
        
        {/* Subtle grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, hsl(var(--primary)) 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 lg:py-40">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div className="text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-card/80 backdrop-blur-sm border border-border rounded-full px-4 py-2 mb-8 shadow-sm animate-fade-in-up">
              <span className="w-2 h-2 bg-success rounded-full animate-pulse" />
              <span className="text-caption text-sm font-body">Staatlich anerkannte Logopädin</span>
            </div>

            {/* Heading */}
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-heading mb-6 animate-fade-in-up animation-delay-100">
              JaNa Logo
              <span className="block text-primary mt-2">Praxis für Logopädie</span>
            </h1>

            {/* Subtitle */}
            <p className="text-body font-body text-lg lg:text-xl mb-8 max-w-xl mx-auto lg:mx-0 animate-fade-in-up animation-delay-200">
              Professionelle logopädische Therapie in Schöppingen – 
              <span className="text-primary font-medium"> einfühlsam</span>, 
              <span className="text-primary font-medium"> individuell</span> und 
              <span className="text-primary font-medium"> patientenorientiert</span>.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fade-in-up animation-delay-300">
              <Button variant="cta" size="xl" onClick={scrollToContact}>
                Termin anfragen
              </Button>
              <Button variant="outline" size="xl" onClick={scrollToNext}>
                Mehr erfahren
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-3 gap-6 mt-12 pt-8 border-t border-border/50 animate-fade-in-up animation-delay-400">
              <div className="text-center lg:text-left">
                <div className="flex items-center justify-center lg:justify-start mb-2">
                  <Heart className="w-5 h-5 text-primary mr-2" />
                  <span className="text-heading font-heading text-2xl font-semibold">100%</span>
                </div>
                <span className="text-caption text-sm font-body">Individuelle Betreuung</span>
              </div>
              <div className="text-center lg:text-left">
                <div className="flex items-center justify-center lg:justify-start mb-2">
                  <Users className="w-5 h-5 text-primary mr-2" />
                  <span className="text-heading font-heading text-2xl font-semibold">Alle</span>
                </div>
                <span className="text-caption text-sm font-body">Altersgruppen</span>
              </div>
              <div className="text-center lg:text-left">
                <div className="flex items-center justify-center lg:justify-start mb-2">
                  <Award className="w-5 h-5 text-primary mr-2" />
                  <span className="text-heading font-heading text-2xl font-semibold">Kassen</span>
                </div>
                <span className="text-caption text-sm font-body">& Privat</span>
              </div>
            </div>
          </div>

          {/* Hero Visual */}
          <div className="relative hidden lg:block">
            <div className="relative">
              {/* Main visual container */}
              <div className="relative w-full aspect-square max-w-lg mx-auto">
                {/* Background decoration */}
                <div className="absolute inset-4 bg-primary-light/40 rounded-3xl transform rotate-6" />
                <div className="absolute inset-4 bg-secondary-light/50 rounded-3xl transform -rotate-3" />
                
                {/* Main card */}
                <div className="relative bg-card rounded-3xl shadow-elegant overflow-hidden border border-border">
                  <div className="aspect-square flex items-center justify-center bg-gradient-to-br from-primary-light via-card to-secondary-light p-12">
                    {/* Abstract speech therapy illustration */}
                    <div className="relative w-full h-full flex items-center justify-center">
                      {/* Speech bubbles */}
                      <div className="absolute top-8 left-8 w-20 h-16 bg-primary/10 rounded-2xl rounded-bl-none animate-pulse" />
                      <div className="absolute top-12 right-12 w-24 h-14 bg-secondary/20 rounded-2xl rounded-br-none animate-pulse animation-delay-200" />
                      <div className="absolute bottom-16 left-16 w-16 h-12 bg-accent-warm/30 rounded-2xl rounded-tl-none animate-pulse animation-delay-300" />
                      
                      {/* Central icon */}
                      <div className="w-32 h-32 bg-primary rounded-full flex items-center justify-center shadow-glow">
                        <svg viewBox="0 0 24 24" fill="none" className="w-16 h-16 text-primary-foreground" stroke="currentColor" strokeWidth="1.5">
                          <path d="M12 18.5a6.5 6.5 0 100-13 6.5 6.5 0 000 13z" />
                          <path d="M12 14v-3M12 9h.01" strokeLinecap="round" />
                          <path d="M8 21h8M10 21v-2.5M14 21v-2.5" strokeLinecap="round" />
                          <path d="M6.5 8.5C5 8.5 3 9.5 3 12s2 3.5 3.5 3.5" strokeLinecap="round" />
                          <path d="M17.5 8.5c1.5 0 3.5 1 3.5 3.5s-2 3.5-3.5 3.5" strokeLinecap="round" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 bg-card rounded-2xl shadow-card p-4 border border-border animate-float">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center">
                    <Heart className="w-5 h-5 text-success" />
                  </div>
                  <div>
                    <p className="text-heading text-sm font-semibold">Einfühlsam</p>
                    <p className="text-caption text-xs">& kompetent</p>
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-4 -left-4 bg-card rounded-2xl shadow-card p-4 border border-border animate-float animation-delay-300">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-heading text-sm font-semibold">Für alle</p>
                    <p className="text-caption text-xs">Altersgruppen</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 inset-x-0 flex justify-center px-4">
        <button
          onClick={scrollToNext}
          className="flex flex-col items-center text-caption hover:text-primary transition-colors duration-300 animate-bounce [animation-duration:0.82s]"
          aria-label="Nach unten scrollen"
        >
          <span className="text-xs font-body mb-2">Mehr entdecken</span>
          <ChevronDown className="w-5 h-5" />
        </button>
      </div>
    </section>
  );
};

export default HeroSection;
